import { useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Upload, FileText, X, Download } from "lucide-react";

/** Universal file uploader (PDF, docx, xlsx, etc). Not for images — use PhotoUploader for those.
 *  Backend accepts any content type up to 20MB.
 */
export default function FileUploader({ fileIds = [], onChange, testIdPrefix = "file-uploader", accept = "*/*" }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [meta, setMeta] = useState({}); // { fid: { name, size, ct } }

  const upload = async (files) => {
    if (!files || files.length === 0) return;
    setBusy(true);
    try {
      const newIds = [];
      const nextMeta = { ...meta };
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const r = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        newIds.push(r.data.id);
        nextMeta[r.data.id] = { name: r.data.original_filename || f.name, size: r.data.size, ct: r.data.content_type };
      }
      setMeta(nextMeta);
      onChange?.([...(fileIds || []), ...newIds]);
      toast.success(`Attached ${newIds.length} file${newIds.length > 1 ? "s" : ""}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };

  const token = localStorage.getItem("opscmd_token");
  const base = process.env.REACT_APP_BACKEND_URL;

  return (
    <div>
      <div className="flex gap-2 items-center">
        <button type="button" data-testid={`${testIdPrefix}-pick`} onClick={() => fileRef.current?.click()}
          className="inline-flex items-center gap-2 border border-[#1a1a1a] text-[#1a1a1a] px-3 py-2 text-xs uppercase tracking-wider font-bold hover:bg-[#1a1a1a] hover:text-white transition-colors duration-150">
          <Upload className="w-3.5 h-3.5" /> Attach files
        </button>
        {busy && <span className="text-[11px] font-mono text-[#1a1a1a]/60">Uploading…</span>}
        <input ref={fileRef} type="file" accept={accept} multiple hidden
               onChange={(e) => upload(Array.from(e.target.files || []))} />
        <span className="text-[10px] font-mono text-[#1a1a1a]/45 uppercase tracking-wider">Any type · 20MB max each</span>
      </div>
      {fileIds.length > 0 && (
        <ul className="mt-3 space-y-1.5" data-testid={`${testIdPrefix}-list`}>
          {fileIds.map((id) => {
            const m = meta[id];
            const label = m?.name || id.slice(0, 8);
            const sizeKb = m?.size ? `${Math.round(m.size / 1024)} KB` : "";
            return (
              <li key={id} className="flex items-center gap-2 bg-white border border-[#1a1a1a]/10 rounded-lg px-2.5 py-1.5" data-testid={`${testIdPrefix}-chip-${id}`}>
                <FileText className="w-3.5 h-3.5 text-[#ff4500] flex-shrink-0" />
                <a href={`${base}/api/files/${id}?auth=${encodeURIComponent(token || "")}`} target="_blank" rel="noreferrer"
                   className="flex-1 text-[12px] text-[#1a1a1a] hover:text-[#ff4500] truncate inline-flex items-center gap-1">
                  {label} {sizeKb && <span className="text-[10px] font-mono text-[#1a1a1a]/40">· {sizeKb}</span>}
                  <Download className="w-3 h-3 opacity-50" />
                </a>
                <button type="button" onClick={() => onChange?.(fileIds.filter((p) => p !== id))}
                        data-testid={`${testIdPrefix}-remove-${id}`}
                        className="text-[#1a1a1a]/40 hover:text-[#ff4500]">
                  <X className="w-3 h-3" />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
